import React, { FC, useMemo } from 'react'
import { ImageBackground } from 'react-native'
import { graphql, useFragment } from 'react-relay'
import urlParser from 'js-video-url-parser'

import { GRADIENT_IMAGE_URL } from '../../data/config/images'
import { TransformImage } from '../GraphQLImage'
import { Button } from '../Button'
import { Icon } from '../Icon'
import { ExternalLink } from '../Link'
import { Stack } from '../Stack'
import { Text } from '../Text'
import { PresentationVideoPlayerYouTubeEmbed } from './VideoPlayerEmbeds/YouTube/PresentationVideoPlayerYouTubeEmbed'

import { PresentationVideoPlayer_presentation$key } from './__generated__/PresentationVideoPlayer_presentation.graphql'

export const PresentationVideoPlayer: FC<{
  presentation: PresentationVideoPlayer_presentation$key
}> = ({ presentation }) => {
  const data = useFragment(
    graphql`
      fragment PresentationVideoPlayer_presentation on Presentation {
        videoSourceUrl
      }
    `,
    presentation
  )

  const { videoInfo, youtubeEmbedUrl } = useMemo(() => {
    if (!data.videoSourceUrl) {
      return {}
    }

    const videoInfo = urlParser.parse(data.videoSourceUrl)
    if (!videoInfo) {
      return {}
    }

    const youtubeEmbedUrl = urlParser.create({
      format: 'embed',
      params: videoInfo.params,
      videoInfo,
    })

    return { videoInfo, youtubeEmbedUrl }
  }, [data.videoSourceUrl])

  if (!data.videoSourceUrl) {
    return null
  }

  switch (videoInfo?.provider) {
    case 'youtube': {
      if (!youtubeEmbedUrl) {
        return null
      }

      return (
        <PresentationVideoPlayerYouTubeEmbed
          youtubeEmbedUrl={youtubeEmbedUrl}
        />
      )
    }
    default:
      return (
        <TransformImage
          as={ImageBackground}
          imageUrl={GRADIENT_IMAGE_URL}
          maxHeight={50}
          maxWidth='max'
          variant='contentCentered'
        >
          <ExternalLink to={data.videoSourceUrl}>
            <Button>
              <Stack direction='horizontal' gap={2}>
                <Icon name='play' variant='button' />
                <Text variant='button'>Check it out</Text>
              </Stack>
            </Button>
          </ExternalLink>
        </TransformImage>
      )
  }
}
